/**
 * Mutations used by the pages. Each one calls the API and then refreshes the
 * cached data, so screens show the new state right away.
 */
import type { LocalizedList, LocalizedText, Phase } from "@liftquest/shared/types";
import type { InferRequestType } from "hono/client";
import { api, call } from "./api";
import { refreshData } from "./query-client";

async function mutate<T>(request: Promise<T>): Promise<T> {
  const result = await request;
  await refreshData();
  return result;
}

/** Like mutate, for changes whose response nobody needs. */
async function change(request: Promise<unknown>): Promise<void> {
  await mutate(request);
}

// --- plans -------------------------------------------------------------------

export const createPlan = (input: { template?: string; name?: string; locale: string }) =>
  mutate(call(api.plans.$post({ json: input })));

export const setActivePlan = (id: string) => change(call(api.plans[":id"].activate.$post({ param: { id } })));

export const duplicatePlan = (id: string, suffix: string) =>
  mutate(call(api.plans[":id"].duplicate.$post({ param: { id }, json: { suffix } })));

export type PlanFileInput = InferRequestType<typeof api.plans.import.$post>["json"]["file"];

/** Creates a plan from a file exported by LiftQuest (validated on the server). */
export const importPlanFile = (file: unknown) =>
  mutate(call(api.plans.import.$post({ json: { file: file as PlanFileInput } })));

export const importSharedPlan = (token: string) => mutate(call(api.shared[":token"].import.$post({ param: { token } })));

export const deletePlan = (id: string) => change(call(api.plans[":id"].$delete({ param: { id } })));

export const updatePlan = (
  id: string,
  json: Partial<{
    name: LocalizedText;
    description: LocalizedText;
    startDate: string;
    trainingDays: number[];
    cardioDays: number[];
    warmup: LocalizedList;
    tips: LocalizedList;
    phases: Phase[];
  }>,
) => change(call(api.plans[":id"].$patch({ param: { id }, json })));

// --- workouts ----------------------------------------------------------------

export const addWorkout = async (planId: string, name: LocalizedText, kind: "strength" | "cardio") =>
  (await mutate(call(api.plans[":id"].workouts.$post({ param: { id: planId }, json: { name, kind } })))).id;

export const updateWorkout = (
  id: string,
  json: { name?: LocalizedText; focus?: LocalizedText; kind?: "strength" | "cardio" },
) => change(call(api.workouts[":id"].$patch({ param: { id }, json })));

export const deleteWorkout = (id: string) => change(call(api.workouts[":id"].$delete({ param: { id } })));

export const moveWorkout = (id: string, direction: "up" | "down") =>
  change(call(api.workouts[":id"].move.$post({ param: { id }, json: { direction } })));

export const addWorkoutExercise = (workoutId: string, exerciseId: string) =>
  change(call(api.workouts[":id"].exercises.$post({ param: { id: workoutId }, json: { exerciseId } })));

export const updateWorkoutExercise = (
  id: string,
  json: Partial<{
    sets: number;
    targetMin: number;
    targetMax: number;
    restSec: number;
    warmupSets: number;
    alternativeExerciseId: string | null;
    alternativeNote: LocalizedText;
    notes: LocalizedText;
  }>,
) => change(call(api["workout-exercises"][":id"].$patch({ param: { id }, json })));

export const removeWorkoutExercise = (id: string) =>
  change(call(api["workout-exercises"][":id"].$delete({ param: { id } })));

export const moveWorkoutExercise = (id: string, direction: "up" | "down") =>
  change(call(api["workout-exercises"][":id"].move.$post({ param: { id }, json: { direction } })));

// --- exercises ---------------------------------------------------------------

export type ExerciseInput = {
  name: LocalizedText;
  type: "reps" | "time" | "cardio";
  muscles: string[];
  equipment: string;
  videoUrl: string | null;
  instructions: LocalizedList;
  setup: LocalizedText;
  weightStep: number;
};

export const createExercise = (json: ExerciseInput) => mutate(call(api.exercises.$post({ json })));
export const updateExercise = (id: string, json: ExerciseInput) =>
  change(call(api.exercises[":id"].$patch({ param: { id }, json })));
export const deleteExercise = (id: string) => change(call(api.exercises[":id"].$delete({ param: { id } })));

// --- profile & sessions --------------------------------------------------------

export const updateProfile = (
  json: Partial<{ beginnerMode: boolean; weeklyGoal: number; soundEnabled: boolean; vibrationEnabled: boolean }>,
) => change(call(api.me.profile.$patch({ json })));

export const deleteAccount = () => call(api.me.$delete());

export const deleteSession = (id: string) => change(call(api.sessions[":id"].$delete({ param: { id } })));

/** Starts (or resumes) a workout; with `startedAt` in the past it is logged afterwards. */
export const startSession = (json: { workoutId: string; startedAt?: string }) => call(api.sessions.$post({ json }));

/** Corrects a logged set (records are re-evaluated on the server). */
export const updateSet = (
  id: string,
  json: Partial<{ weight: number | null; reps: number | null; durationSec: number | null; distanceKm: number | null }>,
) => change(call(api.sets[":id"].$patch({ param: { id }, json })));

export const deleteLoggedSet = (id: string) => change(call(api.sets[":id"].$delete({ param: { id } })));
